/**
 * HomePanel.tsx
 * Landing dashboard shown when the task pane first opens.
 * Shows quick activity shortcuts, room status, and tips.
 */

import React from 'react';
import { motion } from 'framer-motion';
import { LayoutGrid, Cloud, MessageSquare, Pencil, Upload, Sparkles, Video } from 'lucide-react';
import type { PanelId } from './TaskPane';

const QUICK_ACTIONS = [
  { id: 'mc' as PanelId,          label: 'Multiple Choice', icon: LayoutGrid,    color: 'bg-brand-500/15 text-brand-700 border-brand-500/30', emoji: '📊' },
  { id: 'wordcloud' as PanelId,   label: 'Word Cloud',      icon: Cloud,         color: 'bg-orange-500/15 text-orange-700 border-orange-500/30', emoji: '☁️' },
  { id: 'shortanswer' as PanelId, label: 'Short Answer',    icon: MessageSquare, color: 'bg-green-500/15 text-green-700 border-green-500/30', emoji: '✏️' },
  { id: 'drawing' as PanelId,     label: 'Drawing',         icon: Pencil,        color: 'bg-purple-500/15 text-purple-700 border-purple-500/30', emoji: '🎨' },
  { id: 'video' as PanelId,       label: 'Interactive Video', icon: Video,       color: 'bg-red-500/15 text-red-700 border-red-500/30', emoji: '🎬' },
  { id: 'media' as PanelId,       label: 'Media Upload',    icon: Upload,        color: 'bg-blue-500/15 text-blue-700 border-blue-500/30', emoji: '📸' },
  { id: 'ai' as PanelId,          label: 'AI Builder',      icon: Sparkles,      color: 'bg-pink-500/15 text-pink-700 border-pink-500/30', emoji: '🤖' },
];

interface HomePanelProps {
  onNavigate: (panel: PanelId) => void;
}

export default function HomePanel({ onNavigate }: HomePanelProps): React.ReactElement {
  return (
    <div className="p-4 space-y-5">
      {/* Hero */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.05 }}
        className="rounded-2xl bg-gradient-to-br from-brand-600/40 to-brand-900/40 border border-brand-500/20 p-4"
      >
        <div className="text-2xl mb-1">🎓</div>
        <h2 className="text-sm font-bold text-slate-900 mb-1">Welcome, Guro!</h2>
        <p className="text-xs text-slate-600 leading-relaxed">
          Turn any slide into an interactive activity. Pick a quiz type or let AI generate questions for you.
        </p>
      </motion.div>

      {/* Quick actions grid */}
      <div>
        <p className="text-[10px] text-slate-500 uppercase tracking-wider font-semibold mb-2">
          Add Activity to Slide
        </p>
        <div className="grid grid-cols-2 gap-2">
          {QUICK_ACTIONS.map((action, i) => (
            <motion.button
              key={action.id}
              onClick={() => onNavigate(action.id)}
              initial={{ opacity: 0, scale: 0.92 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.06 + i * 0.04 }}
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
              className={`flex flex-col items-center gap-1.5 p-3 rounded-xl border ${action.color} transition-colors`}
            >
              <span className="text-xl">{action.emoji}</span>
              <span className="text-[11px] font-semibold text-center leading-tight">
                {action.label}
              </span>
            </motion.button>
          ))}
        </div>
      </div>

      {/* Tips */}
      <div className="rounded-xl bg-surface-elevated border border-surface-border p-3">
        <p className="text-[10px] text-slate-500 uppercase tracking-wider font-semibold mb-2">💡 Tip</p>
        <p className="text-xs text-slate-600 leading-relaxed">
          Click <strong className="text-slate-700">"Start Class Session"</strong> below to generate a room code your students can join at <strong className="text-brand-600">gurohub.app/join</strong>
        </p>
      </div>
    </div>
  );
}
